import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const outlineBaseline =
  "border border-slate-900/10 bg-card/80 shadow-[0px_3px_7px_-3px_rgba(3,13,35,0.07),inset_1px_2px_1px_rgba(255,255,255,0.4),inset_-1px_-2px_1px_rgba(255,255,255,0.2)] hover:bg-accent/60 hover:text-accent-foreground active:bg-accent";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold leading-4 ring-0 focus-visible:outline-hidden focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border border-blue-600 bg-blue-500 text-white shadow-[0_4px_9px_-3px_rgba(0,4,12,0.14),inset_-1px_-1px_1px_0px_rgba(242,246,252,0.16),inset_1px_1px_1px_0px_rgba(242,246,252,0.07)] hover:bg-blue-600 active:bg-blue-700",
        destructive:
          "border border-destructive bg-destructive text-destructive-foreground shadow-[0_4px_9px_-3px_rgba(0,4,12,0.14),inset_-1px_-1px_1px_0px_rgba(242,246,252,0.16),inset_1px_1px_1px_0px_rgba(242,246,252,0.07)] hover:bg-red-600 active:bg-red-700",
        outline: cn(outlineBaseline),
        secondary: cn(outlineBaseline),
        ghost:
          "hover:bg-accent/60 hover:text-accent-foreground active:bg-accent",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 text-sm [&_svg]:size-4",
        sm: "h-8 gap-1.5 px-3 text-xs [&_svg]:size-3.5",
        lg: "h-10 px-6 text-base [&_svg]:size-4",
        icon: "h-9 w-9 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
